import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { safeHref } from '../lib/html'
import { describeError } from '../lib/describeError'
import { errorCode, getRefreshPages, refreshPage, type DeskResponse, type RefreshCard, type RefreshResult } from './api'
import { etDate } from './labels'
import { pieceHref } from './links'
import { resultLine, upsideLine } from './refreshPages'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const card = 'rounded-xl border border-ink-500 bg-ink-700'
const btnPrimary = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-4 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 ${focus}`

function PageLink({ title, url }: { title: string; url: string }) {
  const href = safeHref(url)
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className={`font-serif text-[20px] font-medium leading-tight text-paper underline-offset-2 hover:underline ${focus}`}>{title}</a>
    : <span className="font-serif text-[20px] font-medium leading-tight text-paper">{title}</span>
}

function RefreshCardRow({ c, path, canPress, running, thisRun, desk, error, onPress }: {
  c: RefreshCard; path: string; canPress: boolean; running: boolean; thisRun: boolean
  desk: DeskResponse | undefined; error: string | null; onPress: (c: RefreshCard) => void
}) {
  const done = (desk?.finished_runs ?? []).filter((r) => r.kind === 'refresh' && r.topic_label === c.title)
    .sort((a, b) => b.run_id - a.run_id)[0]
  const draft = done?.status === 'done' ? done.pieces[0] : undefined
  return (
    <li className={`${card} flex flex-wrap items-center gap-x-[22px] gap-y-3 px-[18px] py-4`}>
      <div className="flex min-w-0 flex-[1_1_380px] flex-col gap-2">
        <PageLink title={c.title} url={c.page_url} />
        <span className="font-ui text-[14px] text-paper-dim">{upsideLine(c)}</span>
        {c.queries.length > 0 && (
          <span className="font-ui text-[13px] text-paper-faint">
            Searches: {c.queries.map((q) => `“${q.query}”`).join(', ')}
          </span>
        )}
        {done?.status === 'failed' && <span role="alert" className="font-ui text-[13px] text-radar-red">Did not finish: {done.error ?? 'no reason was recorded'}</span>}
        {error && <span role="alert" className="font-ui text-[13px] text-radar-red">{error}</span>}
      </div>
      <div className="flex flex-none flex-col items-end gap-1.5">
        {draft ? (
          <Link to={pieceHref(path, draft.id)} className={btnPrimary}>Open the refresh</Link>
        ) : canPress && (
          <button type="button" className={btnPrimary} disabled={running} aria-busy={thisRun} onClick={() => onPress(c)}>
            {thisRun ? 'Refreshing…' : 'Refresh this page'}
          </button>
        )}
        {thisRun && <span className="font-ui text-[12px] text-paper-faint">This takes a few minutes.</span>}
      </div>
    </li>
  )
}

function ResultRow({ r }: { r: RefreshResult }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-4 gap-y-1 font-ui text-[13px] text-paper-dim">
      <span className="text-[14px] font-semibold text-paper">{r.title}</span>
      <span>{etDate(r.published_at)}</span>
      <span>{resultLine(r)}</span>
    </li>
  )
}

/** Improve this page: live pages Google shows on page two or low on page one, a button that writes a
 *  fact-checked refresh into the page's edit draft, and how the refreshed pages have done since. */
export function RefreshPagesSection({ slug, path, canPress, desk }: { slug: string; path: string; canPress: boolean; desk: DeskResponse | undefined }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['editorial', 'refresh-pages', slug], queryFn: () => getRefreshPages(slug), retry: false })
  const [errors, setErrors] = useState<Record<number, string>>({})
  const run = desk?.writing
  const running = !!run?.running
  const press = useMutation({
    mutationFn: (c: RefreshCard) => refreshPage(slug, c.article_id),
    onSuccess: (_r, c) => {
      setErrors((e) => { const { [c.article_id]: _drop, ...rest } = e; return rest })
      void qc.invalidateQueries({ queryKey: ['editorial-desk', slug] })
    },
    onError: (e, c) => {
      // edit_open / already_running / edit_off / switched_off all carry their own message
      setErrors((m) => ({ ...m, [c.article_id]: describeError(e) }))
      if (errorCode(e) === 'already_running') void qc.invalidateQueries({ queryKey: ['editorial-desk', slug] })
    },
  })
  // When a run ends the card list changes (the page now has a draft): read it again.
  const was = useRef(false)
  useEffect(() => {
    if (was.current && !running) void qc.invalidateQueries({ queryKey: ['editorial', 'refresh-pages', slug] })
    was.current = running
  }, [running, qc, slug])

  const d = q.data
  if (!d || (!d.on && d.refreshed.length === 0)) return null
  return (
    <section aria-label="Improve this page" className="flex flex-col gap-3">
      {d.on && (
        <>
          <div className="flex flex-col gap-1">
            <h2 className="m-0 font-serif text-[24px] font-medium text-paper">Improve this page</h2>
            <p className="m-0 max-w-[640px] font-ui text-[14px] text-paper-dim">
              Live pages Google already shows, but low down. Refreshing one writes a fact-checked update into its edit draft. Nothing on the live page changes until you press Save and publish.
            </p>
          </div>
          {d.latest && (
            // the refreshed page drops out of the cards once its draft is open: the way back to it
            <div className={`${card} flex flex-wrap items-center gap-x-[22px] gap-y-3 px-[18px] py-4`}>
              <span className="min-w-0 flex-[1_1_380px] font-ui text-[14px] text-paper-dim">
                Latest refresh: <span className="font-semibold text-paper">{d.latest.title || 'Untitled'}</span>
                {d.latest.finished_at && <> · {etDate(d.latest.finished_at)}</>}
              </span>
              <Link to={pieceHref(path, d.latest.draft_id)} className={btnPrimary}>Open the refresh</Link>
            </div>
          )}
          {d.cards.length === 0 ? (
            <p className="m-0 font-ui text-[14px] text-paper-dim">No pages to refresh right now.</p>
          ) : (
            <ol className="m-0 flex list-none flex-col gap-2.5 p-0">
              {d.cards.map((c) => (
                <RefreshCardRow key={c.article_id} c={c} path={path} canPress={canPress} running={running || press.isPending}
                                thisRun={(running && run?.topic_label === c.title) || (press.isPending && press.variables?.article_id === c.article_id)}
                                desk={desk} error={errors[c.article_id] ?? null} onPress={(x) => press.mutate(x)} />
              ))}
            </ol>
          )}
        </>
      )}
      {d.refreshed.length > 0 && (
        <div className={`${card} flex flex-col gap-3 p-[18px]`}>
          <h3 className="m-0 font-serif text-[20px] font-medium text-paper">Refreshed pages</h3>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">{d.refreshed.map((r) => <ResultRow key={`${r.article_id}-${r.published_at}`} r={r} />)}</ul>
        </div>
      )}
    </section>
  )
}
